import config from "../config";
import { encryptText, pbdkf2 } from "./OstreonEncryption";

const base_url = config.base_url;

/*
  Account recovery key — the code a user enters to get back in after forgetting
  their password, and the only thing that keeps their data readable when they do.

  A customer gets one at registration. A partner does not: their account is
  created by an admin, and the recovery key is issued at /partner/start, the
  once-only screen where they set their first password. A partner who never went
  through it -- or whose record was edited by hand -- has none at all, and a
  forgotten password would cost them everything they can read.

  This creates one when it is missing. It is not shown at the moment it is made,
  which is fine: `recovery_key_private_key` is the code encrypted under the
  user's own data key, so My Account can reveal it whenever they ask. What
  matters is that it exists before it is needed.
*/

/*
  Same shape as the code the rest of the app issues -- 32 characters from that
  alphabet, in dash-separated groups of four -- but drawn from the platform CSPRNG.

  Register.jsx's generateKey uses Math.random(), which is not suitable for a
  credential: its output is predictable from a handful of prior values. That one
  is left alone here because changing it is a product-wide decision, but nothing
  argues for repeating it in new code.
*/
const CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";

const generateRecoveryKey = () => {
  const bytes = new Uint32Array(32);
  crypto.getRandomValues(bytes);
  let out = "";
  for (let i = 0; i < 32; i++) {
    /*
      256 is not a multiple of 36, so taking a byte modulo 36 would favour the
      first four characters. Drawing from a 32-bit value and rejecting the tail
      above the largest exact multiple keeps every character equally likely.
    */
    let v = bytes[i];
    const limit = Math.floor(0xffffffff / CHARS.length) * CHARS.length;
    while (v >= limit) {
      const extra = new Uint32Array(1);
      crypto.getRandomValues(extra);
      v = extra[0];
    }
    out += CHARS[v % CHARS.length];
  }
  return out.replace(/(.{4})/g, "$1-").slice(0, -1);
};

const post = (path, body) =>
  fetch(base_url + path, {
    method: "POST",
    mode: "cors",
    headers: {
      Accept: "application/json, text/plain, */*",
      "Content-type": "application/x-www-form-urlencoded",
      credentials: "include",
    },
    credentials: "include",
    body,
  });

/*
  Returns the two fields to merge onto CustomerUser, or null when there is
  nothing to do -- the user already has a recovery key, or something failed.

  The caller persists them, because CustomerUser lives in DataContext and saving
  it is that context's job.

  Order matters where this is used: the recovery key has to exist before a
  partner's sharing keypair is created, so the keypair can be wrapped under it
  and survive a forgotten password.
*/
export const ensureRecoveryKey = async (private_key, customerUser) => {
  try {
    if (!private_key || !customerUser) return null;
    // Already has one; nothing to do, and never overwrite it -- that would
    // invalidate the code the user may already have written down.
    if (customerUser.recovery_key_private_key) return null;

    const recovery_key = generateRecoveryKey();
    const [key_pbdkf] = await pbdkf2(recovery_key, "OSTREON", 100000, 32 * 8);

    /*
      The server stores only a hash of the code, for checking it at reset. Sent
      first: if this fails there is no point encrypting anything, and a stored
      wrap whose code the server cannot verify would be worse than none.
    */
    const res = await post(
      "user/recoverykey/update",
      new URLSearchParams({ recovery_key: key_pbdkf }).toString()
    );
    if (res.status !== 200) return null;

    return {
      // The data key, so the code can open the account.
      private_key_recovery_key: await encryptText(recovery_key, private_key),
      // The code, so the signed-in user can be shown it later.
      recovery_key_private_key: await encryptText(private_key, recovery_key),
      recovery_key,
    };
  } catch (error) {
    // Never fatal: the user keeps working, and the next sign-in tries again.
    console.log(error);
    return null;
  }
};
